import { PAGE_REQUEST } from 'store/app/actions';
import type { RootState } from 'store/configureStore';

import { types } from './actions';
import { PANEL_STATES } from './constants';
import { MinimalProductInfo } from './types';

export interface RealTimeMaterialPricingModalState {
  activeVendorMaterialPricingPlanId: number | null;
  allowRequoting: boolean;
  componentId: number | null;
  focusedComponentQuantityId: number | null;
  isOpen: boolean;
  operationId: number | null;
  panelState: keyof typeof PANEL_STATES;
  materialSelections: Record<string, MinimalProductInfo | undefined>;
  materialIds: number[] | null;
  source: string | null;
}

const initialState: RealTimeMaterialPricingModalState = {
  activeVendorMaterialPricingPlanId: null,
  allowRequoting: true,
  componentId: null,
  focusedComponentQuantityId: null,
  isOpen: false,
  operationId: null,
  panelState: PANEL_STATES.MATERIAL_SELECT,
  source: null,
  materialSelections: {},
  materialIds: null,
};

const reducer = (
  state = initialState,
  action,
): RealTimeMaterialPricingModalState => {
  const { type, payload } = action;

  switch (type) {
    case PAGE_REQUEST:
      return initialState;
    case types.ACTIVE_VENDOR_MATERIAL_PRICING_PLAN_SET:
      return {
        ...state,
        activeVendorMaterialPricingPlanId:
          payload.activeVendorMaterialPricingPlanId,
      };
    case types.PANEL_STATE_SET:
      return {
        ...state,
        panelState: payload.panelState,
        activeVendorMaterialPricingPlanId:
          payload.panelState === PANEL_STATES.MATERIAL_SELECT
            ? null
            : state.activeVendorMaterialPricingPlanId,
      };
    case types.SELECTED_PRODUCT_CODE_SET:
      return {
        ...state,
        materialIds: payload.materialIds,
      };
    case types.REAL_TIME_MATERIAL_PRICING_MODAL_CLOSE:
      return {
        ...state,
        isOpen: false,
      };
    case types.REAL_TIME_MATERIAL_PRICING_MODAL_OPEN:
      return {
        ...initialState,
        activeVendorMaterialPricingPlanId:
          payload.vendorMaterialPricingPlanId ?? null,
        allowRequoting: payload.allowRequoting ?? true,
        componentId: payload.componentId,
        focusedComponentQuantityId: payload.focusedComponentQuantityId,
        isOpen: true,
        operationId: payload.operationId,
        panelState: payload.vendorMaterialPricingPlanId
          ? PANEL_STATES.VENDOR_QUOTE
          : initialState.panelState,
        source: payload.source,
      };
    case types.SELECT_MATERIALS:
      return {
        ...state,
        materialSelections: {
          ...state.materialSelections,
          ...payload.materialSelections,
        },
      };
    default:
      return state;
  }
};

export default reducer;

const getScope = (state: RootState): RealTimeMaterialPricingModalState =>
  state.modules.common.modals.realTimeMaterialPricingModal;

export const getActiveVendorMaterialPricingPlanId = (state: RootState) =>
  getScope(state).activeVendorMaterialPricingPlanId;

export const getAllowRequoting = (state: RootState) =>
  getScope(state).allowRequoting;
export const getIsOpen = (state: RootState) => getScope(state).isOpen;

export const getComponentId = (state: RootState) => getScope(state).componentId;

export const getOperationId = (state: RootState) => getScope(state).operationId;
export const getPanelState = (state: RootState) => getScope(state).panelState;

export const getMaterialIds = (state: RootState) =>
  Object.values(getScope(state).materialSelections)
    .filter((materialSelection) => Boolean(materialSelection))
    .map((materialSelection) => materialSelection?.id);

export const getSelectedMaterials = (state: RootState) =>
  getScope(state).materialSelections;
